/* eslint-disable indent */
import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';

import { buildColumnOrderFromCookie } from '../utils/buildColumnOrderFromCookie';
import { buildColumnVisibilityFromCookie } from '../utils/buildColumnVisibilityFromCookie';
import { buildTableSettingsColumns } from '../utils/buildTableSettingsColumns';

const initialFilters = {};

const TableContext = createContext({
  filtersActiveSubmenu: null,
  setFiltersActiveSubmenu: () => {},
});

export const TableProvider = ({ columns, selectedColumns, children }) => {
  const [isReady, setReady] = useState(false);
  const [currentColumns, setStateCurrentColumns] = useState(columns);
  const [currentSelectedColumns, setCurrentSelectedColumns] =
    useState(selectedColumns);
  const [tableContext, setTableContext] = useState(null);
  const [currentColumnOrder, setCurrentColumnOrder] = useState([]);
  const [currentHiddenColumns, setCurrentHiddenColumns] = useState([]);
  const [filtersActiveSubmenu, setFiltersActiveSubmenu] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  // eslint-disable-next-line no-unused-vars
  const [_searchValue, setSearchValue] = useState(null);
  const [selectedSearchValue, setSelectedSearchValue] = useState(null);
  const { pathname } = useLocation();
  const [cookie, setCookie] = useCookies();
  const [selectedRows, setSelectedRows] = useState(null);

  useEffect(() => {
    setReady(false);
    if (cookie[pathname]) {
      setCurrentColumnOrder(buildColumnOrderFromCookie(cookie[pathname]));
    } else {
      setReady(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (!cookie[pathname] && currentColumns) {
      setCookie(pathname, buildTableSettingsColumns(currentColumns));
    }
  }, [currentColumns]);

  useEffect(() => {
    if (tableContext) {
      setReady(true);
      tableContext.setColumnOrder(currentColumnOrder);
    }
  }, [currentColumnOrder, tableContext]);

  useEffect(() => {
    if (tableContext) {
      tableContext.setHiddenColumns(currentHiddenColumns);
    }
  }, [currentHiddenColumns, tableContext]);

  const setQueryFilter = (queryFilter) => {
    setFilters({
      ...filters,
      ...queryFilter,
    });
  };

  const setOptionFilters = (optionFilters) => {
    setFilters({
      ...filters,
      ...optionFilters,
    });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  // eslint-disable-next-line no-shadow
  const setCurrentColumns = (columns) => {
    const internalColumns = [...columns];

    const columnsFromCookie = cookie[pathname]
      ? [...cookie[pathname]]
      : undefined;
    // eslint-disable-next-line no-unused-vars,no-restricted-syntax
    for (const [groupIndex, group] of columns.entries()) {
      // eslint-disable-next-line no-unused-vars,no-restricted-syntax
      for (const [itemIndex, item] of group.columns.entries()) {
        const { defaultVisible } =
          internalColumns[groupIndex].columns[itemIndex];
        // Verify if default visible is provided, if not make it visible by default
        const isShouldBeVisible =
          defaultVisible === false ? defaultVisible : true;
        internalColumns[groupIndex].columns[itemIndex].visible =
          columnsFromCookie
            ? columnsFromCookie[groupIndex].order.find(
                (cookieColumn) => cookieColumn.id === item.id,
              )?.visible
            : isShouldBeVisible;
      }
    }
    setStateCurrentColumns(internalColumns);
  };

  const resetSearchValue = () => {
    setSearchValue(null);
  };

  // That solution is cookie based
  // When table settings updated, that new settings automatically stored in cookies
  // It may be done from different places, so listen to changes then apply to table
  useEffect(() => {
    if (cookie[pathname]) {
      setCurrentColumnOrder(buildColumnOrderFromCookie(cookie[pathname]));
      setCurrentHiddenColumns(
        buildColumnVisibilityFromCookie(cookie[pathname]),
      );
    }
  }, [cookie]);

  const resetSelectedRows = () =>
    tableContext?.toggleAllPageRowsSelected(false);

  const contextValue = useMemo(
    () => ({
      isReady,
      setReady,
      currentColumns,
      setCurrentColumns,
      currentSelectedColumns,
      setCurrentSelectedColumns,
      tableContext,
      setTableContext,
      currentColumnOrder,
      setCurrentColumnOrder,
      setCurrentHiddenColumns,
      currentHiddenColumns,
      filtersActiveSubmenu,
      setFiltersActiveSubmenu,
      filters,
      setQueryFilter,
      setOptionFilters,
      resetFilters,
      selectedSearchValue,
      setSelectedSearchValue,
      resetSearchValue,
      selectedRows,
      setSelectedRows,
      resetSelectedRows,
    }),
    [
      isReady,
      setReady,
      currentColumns,
      setCurrentColumns,
      currentSelectedColumns,
      setCurrentSelectedColumns,
      tableContext,
      setTableContext,
      currentColumnOrder,
      setCurrentColumnOrder,
      setCurrentHiddenColumns,
      currentHiddenColumns,
      filtersActiveSubmenu,
      setFiltersActiveSubmenu,
      filters,
      setQueryFilter,
      setOptionFilters,
      resetFilters,
      selectedSearchValue,
      setSelectedSearchValue,
      resetSearchValue,
      selectedRows,
      setSelectedRows,
      resetSelectedRows,
    ],
  );

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
};

export const useTableSettings = () => useContext(TableContext);
// Just for name convention
export const useTableFilters = () => useContext(TableContext);

export const useTableSearch = () => useContext(TableContext);

export const useTableSelection = () => useContext(TableContext);

TableProvider.propTypes = {
  columns: PropTypes.array,
  selectedColumns: PropTypes.array,
  children: PropTypes.node,
};
